import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Volume2, VolumeX, Loader, AlertCircle, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Configuración de servidores TTS
const TTS_SERVERS = {
  edge: process.env.REACT_APP_TTS_SERVER_URL || 'http://localhost:8001',
  higgs: process.env.REACT_APP_HIGGS_SERVER_URL || 'http://localhost:8003',
  qwen: 'http://localhost:8004' // Default, can be overridden by qwenServerUrl prop
};
// Forzar uso del servidor local en desarrollo
const USE_NETLIFY_PROXY = process.env.NODE_ENV === 'production' && !process.env.REACT_APP_TTS_SERVER_URL;

function TTSButton({
  text,
  darkMode,
  className = '',
  engine = 'edge',        // 'edge' | 'higgs' | 'qwen'
  voice = 'es-MX-DaliaNeural',
  language = 'es',
  speed = 1.0,
  rate = '+0%',           // Velocidad Edge-TTS
  volume = '+0%',         // Volumen Edge-TTS
  pitch = '+0Hz',         // Pitch Edge-TTS
  useNaturalSpeed = false,
  // Higgs Audio params
  temperature = 1.0,      // Aleatoriedad
  seed = null,            // Seed para reproducibilidad
  voiceId = null,         // ID de voz clonada
  higgsEmotion = 'neutral', // neutral, happy, sad, angry, surprised
  higgsTopP = 0.9,          // Nucleus sampling (0.5-1.0)
  higgsPlaybackSpeed = 1.0, // Velocidad de reproducción (playbackRate)
  // Qwen3-TTS params
  qwenMode = 'voice_clone', // Solo voice_clone soportado en servidor producción (8004)
  qwenSpeaker = 'Ryan',     // Voz predefinida de Qwen3 (solo custom_voice)
  qwenVoiceId = null,       // ID de voz clonada (solo voice_clone)
  qwenInstruct = '',        // Instrucción en lenguaje natural (solo custom_voice)
  qwenEmotion = 'neutral',  // Preset de emoción (solo custom_voice)
  qwenTemperature = 0.7,    // Aleatoriedad (0.1-1.5)
  qwenTopP = 0.9,           // Nucleus sampling (0.5-1.0)
  qwenSeed = null,          // Seed para reproducibilidad
  qwenPlaybackSpeed = 1.0,  // Velocidad de reproducción (playbackRate)
  // Qwen Voice Design params
  qwenVoiceDesignPreset = 'male_professional',  // Preset de voice design
  qwenVoiceDesignPrompt = '',  // Prompt personalizado para crear voz
  qwenServerUrl = null,       // URL del servidor Qwen (8004 o 8006)
  questionId = null,
  showLabel = true,
  size = 'normal' // 'small', 'normal', 'large'
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [hasGenerated, setHasGenerated] = useState(false); // Track if audio was generated

  const audioRef = useRef(null);
  const abortControllerRef = useRef(null);
  const cacheKeyRef = useRef(null);
  const forceRegenerateRef = useRef(false);

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Limpiar audio cache cuando cambia el motor o la voz clonada
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    cacheKeyRef.current = null;
    setIsPlaying(false);
    setProgress(0);
    setHasGenerated(false);
  }, [engine, voiceId, qwenMode, qwenVoiceId]);

  // Ajustar playbackRate según el motor
  useEffect(() => {
    if (audioRef.current) {
      if (engine === 'higgs') {
        // Higgs usa su propio playbackSpeed
        audioRef.current.playbackRate = higgsPlaybackSpeed || 1.0;
      } else if (engine === 'qwen') {
        // Qwen usa su propio playbackSpeed
        audioRef.current.playbackRate = qwenPlaybackSpeed || 1.0;
      } else if (!useNaturalSpeed) {
        // Edge-TTS usa speed (si no es velocidad natural)
        audioRef.current.playbackRate = speed || 1.0;
      }
    }
  }, [speed, useNaturalSpeed, engine, higgsPlaybackSpeed, qwenPlaybackSpeed]);

  // Obtener tamaño del icono según el prop size
  const getIconSize = () => {
    switch(size) {
      case 'small': return 'w-4 h-4';
      case 'large': return 'w-6 h-6';
      default: return 'w-5 h-5';
    }
  };

  // Obtener padding según el tamaño
  const getPadding = () => {
    switch(size) {
      case 'small': return 'p-2';
      case 'large': return 'p-3';
      default: return 'p-2.5';
    }
  };

  // Función de fallback usando Web Speech API del navegador
  const playBrowserTTS = (text) => {
    try {
      // Cancelar cualquier reproducción anterior
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = language || 'es-MX';
      utterance.rate = speed || 1.0;

      // Buscar voz en español si está disponible
      const voices = window.speechSynthesis.getVoices();
      const spanishVoice = voices.find(v => v.lang.startsWith('es'));
      if (spanishVoice) {
        utterance.voice = spanishVoice;
      }

      utterance.onstart = () => setIsPlaying(true);
      utterance.onend = () => {
        setIsPlaying(false);
        setProgress(0);
      };
      utterance.onerror = (e) => {
        console.error('Browser TTS error:', e);
        setError('Error en síntesis de voz del navegador');
        setIsPlaying(false);
      };

      window.speechSynthesis.speak(utterance);
      console.log('Usando TTS del navegador como fallback');
      return true;
    } catch (err) {
      console.error('Browser TTS not available:', err);
      return false;
    }
  };

  const handlePlay = async (forceRegenerate = false) => {
    // Si ya está reproduciendo, pausar
    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
      } else {
        window.speechSynthesis.cancel();
      }
      setIsPlaying(false);
      return;
    }

    // Si ya tenemos audio cargado y no estamos forzando regeneración, continuar reproducción
    if (audioRef.current && audioRef.current.src && !forceRegenerate) {
      // Ajustar playbackRate según el motor
      if (engine === 'higgs') {
        audioRef.current.playbackRate = higgsPlaybackSpeed || 1.0;
      } else if (engine === 'qwen') {
        audioRef.current.playbackRate = qwenPlaybackSpeed || 1.0;
      } else if (!useNaturalSpeed) {
        audioRef.current.playbackRate = speed || 1.0;
      }
      audioRef.current.play();
      setIsPlaying(true);
      return;
    }

    // Limpiar audio anterior si estamos regenerando
    if (forceRegenerate && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      cacheKeyRef.current = null;
    }

    // Generar nuevo audio
    setIsLoading(true);
    setError(null);
    setProgress(0);
    forceRegenerateRef.current = forceRegenerate;

    try {
      // Crear controlador para poder cancelar la petición
      abortControllerRef.current = new AbortController();

      // Preparar el texto (limpiar HTML si existe)
      const cleanText = text.replace(/<[^>]*>/g, '').trim();

      // Determinar servidor y endpoint según el motor
      // Para Qwen, usar qwenServerUrl si está definido (permite cambiar entre 8004/8006)
      const serverUrl = (engine === 'qwen' && qwenServerUrl) ? qwenServerUrl : (TTS_SERVERS[engine] || TTS_SERVERS.edge);
      const ttsEndpoint = USE_NETLIFY_PROXY
        ? '/.netlify/functions/tts-proxy'
        : `${serverUrl}/api/tts/generate`;

      // Construir body según el motor
      let requestBody;
      if (engine === 'qwen') {
        // Qwen3-TTS - Ultra-baja latencia, control por instrucciones o voice cloning
        requestBody = {
          text: cleanText,
          language,
          mode: qwenMode,
          temperature: qwenTemperature,
          top_p: qwenTopP,
          seed: qwenSeed,
          cache: true,
          force_regenerate: forceRegenerateRef.current
        };
        // Añadir parámetros específicos según el modo
        if (qwenMode === 'voice_clone') {
          requestBody.voice_id = qwenVoiceId;
        } else if (qwenMode === 'voice_design') {
          // Voice Design mode - crea voz desde descripción
          requestBody.voice_design_preset = qwenVoiceDesignPreset;
          requestBody.voice_design_prompt = qwenVoiceDesignPrompt || null;
          // Configuraciones óptimas para voice design
          requestBody.temperature = 0.9;
          requestBody.top_p = 1.0;
          requestBody.top_k = 50;
          requestBody.repetition_penalty = 1.05;
        } else {
          // custom_voice mode
          requestBody.speaker = qwenSpeaker;
          requestBody.instruct = qwenInstruct || null;
          requestBody.emotion = qwenEmotion;
        }
      } else if (engine === 'higgs') {
        // Higgs Audio v2 - Voice cloning, emociones
        // NOTA: speed se eliminó - se controla con playbackRate en el reproductor
        requestBody = {
          text: cleanText,
          language,
          voice_id: voiceId,
          emotion: higgsEmotion,
          temperature,
          top_p: higgsTopP,
          seed,
          cache: true,
          force_regenerate: forceRegenerateRef.current
        };
      } else {
        // Edge-TTS - Rápido, buena calidad
        requestBody = {
          text: cleanText,
          engine: USE_NETLIFY_PROXY ? 'openai' : 'indextts',
          voice,
          language,
          speed,
          rate,
          volume,
          pitch,
          cache: !USE_NETLIFY_PROXY
        };
      }

      // Debug
      console.log('TTS Config:', {
        engine,
        serverUrl,
        ttsEndpoint,
        text: cleanText.substring(0, 50) + '...'
      });

      // Hacer petición al servidor TTS
      const response = await fetch(ttsEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        let errorMessage = 'Error generando audio';
        try {
          const errorData = await response.json();
          errorMessage = typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail) || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = `Error ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      let audioBlob;
      let audioUrl;

      if (USE_NETLIFY_PROXY) {
        // En producción, la función devuelve el audio directamente
        audioBlob = await response.blob();
        audioUrl = URL.createObjectURL(audioBlob);
      } else {
        // En desarrollo, usar el servidor TTS local
        const data = await response.json();
        cacheKeyRef.current = data.audio_url.split('/').pop();

        // Obtener el audio del servidor correspondiente
        const audioResponse = await fetch(`${serverUrl}${data.audio_url}`, {
          signal: abortControllerRef.current.signal
        });

        if (!audioResponse.ok) {
          throw new Error('Error descargando audio');
        }

        audioBlob = await audioResponse.blob();
        audioUrl = URL.createObjectURL(audioBlob);
      }

      // Crear elemento de audio
      audioRef.current = new Audio(audioUrl);
      // Ajustar playbackRate según el motor
      if (engine === 'higgs') {
        audioRef.current.playbackRate = higgsPlaybackSpeed || 1.0;
      } else if (engine === 'qwen') {
        audioRef.current.playbackRate = qwenPlaybackSpeed || 1.0;
      } else if (!useNaturalSpeed) {
        audioRef.current.playbackRate = speed || 1.0;
      }

      // Configurar eventos
      audioRef.current.onplay = () => setIsPlaying(true);
      audioRef.current.onpause = () => setIsPlaying(false);
      audioRef.current.onended = () => {
        setIsPlaying(false);
        setProgress(0);
      };

      // Actualizar progreso
      audioRef.current.ontimeupdate = () => {
        if (audioRef.current.duration) {
          const progressPercent = (audioRef.current.currentTime / audioRef.current.duration) * 100;
          setProgress(progressPercent);
        }
      };

      audioRef.current.onerror = (e) => {
        console.error('Error reproduciendo audio:', e);
        setError('Error reproduciendo audio');
        setIsPlaying(false);
      };

      // Reproducir
      await audioRef.current.play();
      setHasGenerated(true); // Marcar que se generó audio exitosamente

    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Petición TTS cancelada');
      } else {
        console.error('Error en TTS:', err);

        // Intentar fallback con Browser TTS si el servidor no está disponible
        const fallbackEnabled = process.env.REACT_APP_TTS_FALLBACK_TO_BROWSER !== 'false';
        if (fallbackEnabled && (err.message.includes('fetch') || err.message.includes('network'))) {
          console.log('Servidor TTS no disponible, usando fallback del navegador');
          setIsLoading(false);
          const cleanText = text.replace(/<[^>]*>/g, '').trim();
          if (playBrowserTTS(cleanText)) {
            setError(null); // Limpiar error si el fallback funciona
            return;
          }
        }

        setError(err.message || 'Error generando audio');
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
      forceRegenerateRef.current = false;
    }
  };

  // Función para regenerar audio
  const handleRegenerate = () => {
    handlePlay(true);
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    // También detener Browser TTS si está activo
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setProgress(0);
  };

  // Determinar qué icono mostrar
  const getIcon = () => {
    if (isLoading) {
      return <Loader className={`${getIconSize()} animate-spin`} />;
    }
    if (error) {
      return <AlertCircle className={`${getIconSize()} text-red-500`} />;
    }
    if (isPlaying) {
      return <VolumeX className={getIconSize()} />;
    }
    return <Volume2 className={getIconSize()} />;
  };

  // Determinar el color del botón
  const getButtonColor = () => {
    if (error) {
      return darkMode
        ? 'bg-red-900/50 hover:bg-red-800/60 border-red-700/50'
        : 'bg-red-100 hover:bg-red-200 border-red-300';
    }
    if (isPlaying) {
      return darkMode
        ? 'bg-cyan-600/50 hover:bg-cyan-500/60 border-cyan-400/50 shadow-[0_0_15px_rgba(6,182,212,0.4)]'
        : 'bg-blue-500 hover:bg-blue-600 text-white';
    }
    if (isLoading) {
      return darkMode
        ? 'bg-gray-700/50 border-gray-600/50'
        : 'bg-gray-200 border-gray-300';
    }
    return darkMode
      ? 'bg-slate-700/50 hover:bg-slate-600/60 border-slate-600/50 hover:shadow-lg hover:shadow-blue-500/10'
      : 'bg-white hover:bg-gray-50 border-gray-300 hover:border-blue-400';
  };

  return (
    <div className={`relative inline-flex items-center ${className}`}>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => handlePlay(false)}
        onDoubleClick={handleStop}
        disabled={isLoading}
        className={`
          relative flex items-center gap-2 ${getPadding()}
          ${getButtonColor()}
          ${darkMode ? 'text-gray-100' : 'text-gray-700'}
          rounded-lg border-2 transition-all duration-300
          ${!isLoading && !error ? 'cursor-pointer' : ''}
          ${isLoading ? 'cursor-wait opacity-70' : ''}
          ${error ? 'cursor-help' : ''}
        `}
        title={
          error ? `Error: ${error}` :
          isLoading ? 'Generando audio...' :
          isPlaying ? 'Click para pausar, doble click para detener' :
          'Escuchar texto'
        }
      >
        {/* Barra de progreso */}
        {isPlaying && progress > 0 && (
          <div
            className="absolute bottom-0 left-0 h-0.5 bg-cyan-400 transition-all duration-300 rounded-b"
            style={{ width: `${progress}%` }}
          />
        )}

        {/* Icono */}
        <AnimatePresence mode="wait">
          <motion.div
            key={isLoading ? 'loading' : isPlaying ? 'playing' : 'idle'}
            initial={{ opacity: 0, rotate: -180 }}
            animate={{ opacity: 1, rotate: 0 }}
            exit={{ opacity: 0, rotate: 180 }}
            transition={{ duration: 0.2 }}
          >
            {getIcon()}
          </motion.div>
        </AnimatePresence>

        {/* Label opcional */}
        {showLabel && !isLoading && (
          <span className={`text-sm font-medium ${size === 'small' ? 'hidden sm:inline' : ''}`}>
            {isPlaying ? 'Pausar' : 'Escuchar'}
          </span>
        )}

        {/* Spinner de carga */}
        {isLoading && showLabel && (
          <span className="text-sm">Generando...</span>
        )}
      </motion.button>

      {/* Botón regenerar (Edge-TTS, Higgs y Qwen) */}
      {hasGenerated && !isLoading && (
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleRegenerate}
          className={`
            ml-1 p-1.5 rounded-lg transition-all
            ${engine === 'qwen'
              ? darkMode
                ? 'bg-green-600/50 hover:bg-green-500/60 text-green-200'
                : 'bg-green-100 hover:bg-green-200 text-green-700'
              : engine === 'higgs'
                ? darkMode
                  ? 'bg-amber-600/50 hover:bg-amber-500/60 text-amber-200'
                  : 'bg-amber-100 hover:bg-amber-200 text-amber-700'
                : darkMode
                    ? 'bg-cyan-600/50 hover:bg-cyan-500/60 text-cyan-200'
                    : 'bg-cyan-100 hover:bg-cyan-200 text-cyan-700'}
          `}
          title={
            engine === 'qwen'
              ? "Regenerar audio (nueva generación Qwen)"
              : engine === 'higgs'
                ? "Regenerar audio (nueva generación Higgs)"
                : useNaturalSpeed
                  ? "Regenerar audio (velocidad natural IA)"
                  : "Regenerar audio"
          }
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </motion.button>
      )}

      {/* Tooltip de error */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`
              absolute bottom-full mb-2 left-0
              px-3 py-2 rounded-lg text-sm whitespace-nowrap
              ${darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'}
              shadow-lg z-50
            `}
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
            <div className={`
              absolute top-full left-4
              w-0 h-0
              border-l-[6px] border-l-transparent
              border-r-[6px] border-r-transparent
              border-t-[6px] ${darkMode ? 'border-t-red-900' : 'border-t-red-100'}
            `} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Indicador de audio en cache */}
      {cacheKeyRef.current && !isLoading && !error && (
        <div
          className={`
            absolute -top-1 -right-1 w-2 h-2 rounded-full
            ${darkMode ? 'bg-green-400' : 'bg-green-500'}
            animate-pulse
          `}
          title="Audio en cache"
        />
      )}
    </div>
  );
}

// Componente para controles de velocidad
export function TTSSpeedControl({ speed, onChange, darkMode }) {
  const speeds = [
    { value: 0.75, label: '0.75x' },
    { value: 1.0, label: '1x' },
    { value: 1.25, label: '1.25x' },
    { value: 1.5, label: '1.5x' },
  ];

  return (
    <div className="flex items-center gap-1">
      {speeds.map((s) => (
        <button
          key={s.value}
          onClick={() => onChange(s.value)}
          className={`
            px-2 py-1 text-xs rounded transition-all
            ${speed === s.value
              ? darkMode
                ? 'bg-cyan-600 text-white'
                : 'bg-blue-500 text-white'
              : darkMode
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }
          `}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}

export default TTSButton;
